import { useCallback, useState } from 'react'
import type { Account } from '@domain/finance'
import type { NewAccount, ProviderAccount } from '@domain/ports/finance-repository'
import { centsToInput, parseAmountInput } from '@shared/money/parse'
import { formatBRL } from '@shared/money/format'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Problem, ProviderLinkField, SelectField, TextField } from './CrudeFields'
import { ACCOUNT_TYPES, STATUSES, optionsOf } from './labels'
import { useCollection } from './use-collection'

/**
 * CRUD de contas, cru. Serve para exercitar o modelo, não para ser usado — as
 * telas de verdade são a Fase 4.
 *
 * ## As duas regras que esta tela existe para cumprir
 *
 * **1. `type: 'cash'` não tem saldo.** `Docs/02-Dominio/Account.md` diz que não
 * há como saber quanto há no bolso, e o adapter cobra isso **no `create`** e
 * escreve, no próprio código, que **não** cobra no `update` — a checagem
 * precisaria do `type` já gravado, que o patch não carrega. O comentário de lá
 * termina com "quem fecha é a tela da Task 11, não oferecendo o campo de saldo
 * para 'cash'". É esta linha: o campo **desaparece** quando o tipo é dinheiro,
 * na criação e na edição.
 *
 * **2. Dinheiro entra como texto e sai em centavos inteiros.** `parseAmountInput`
 * é a conversão, e ela não usa float em lugar nenhum. Campo em branco vira
 * `null` — que é "saldo desconhecido", diferente de `0`.
 */

const ID = 'contas'

type Draft = {
  name: string
  institution: string
  type: Account['type']
  balance: string
}

const EMPTY: Draft = { name: '', institution: '', type: 'checking', balance: '' }

function draftOf(account: Account): Draft {
  return {
    name: account.name,
    institution: account.institution,
    type: account.type,
    balance: account.balanceCents === null ? '' : centsToInput(account.balanceCents),
  }
}

export function AccountsScreen() {
  const { accountRepository, providerAccountRepository } = useFinanceRepositories()
  const load = useCallback(() => accountRepository.list(), [accountRepository])
  const { state, reload } = useCollection<Account>(load)
  const loadProvider = useCallback(
    () => providerAccountRepository.list(),
    [providerAccountRepository],
  )
  const catalogo = useCollection<ProviderAccount>(loadProvider)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [failure, setFailure] = useState<string | null>(null)

  // Dinheiro em espécie não tem saldo consultável: o campo não é desabilitado,
  // ele não existe. Um campo desabilitado ainda carregaria o valor digitado
  // antes da troca de tipo, e ele iria junto no `create`.
  const hasBalance = draft.type !== 'cash'
  const parsedBalance = parseAmountInput(draft.balance)
  const balanceProblem =
    hasBalance && parsedBalance.status === 'invalid' ? parsedBalance.message : null

  function change(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setFailure(null)
  }

  function reset() {
    setEditingId(null)
    setDraft(EMPTY)
    setFailure(null)
  }

  async function submit() {
    setFailure(null)
    if (balanceProblem !== null) return

    // Trocar para 'cash' com um valor já digitado no campo: o saldo vai como
    // `null`, e não como o número que sobrou na tela.
    const balanceCents = hasBalance && parsedBalance.status === 'ok' ? parsedBalance.cents : null

    try {
      if (editingId === null) {
        const input: NewAccount = {
          name: draft.name,
          institution: draft.institution,
          type: draft.type,
          balanceCents,
        }
        await accountRepository.create(input)
      } else {
        await accountRepository.update(editingId, {
          name: draft.name,
          institution: draft.institution,
          balanceCents,
        })
      }
      reset()
      reload()
    } catch (error: unknown) {
      console.error('AccountsScreen: a escrita falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível salvar a conta.')
    }
  }

  /**
   * O vínculo é gravado na hora, sem passar pelo formulário de edição.
   *
   * Ele não é dado do formulário: não se digita, se escolhe de uma lista que o
   * sync publicou. Levá-lo para o rascunho faria uma escolha de um clique
   * exigir "editar → escolher → salvar", e o campo ficaria pendurado num
   * `Draft` que o `create` não usa (o vínculo é só do patch — ver a porta).
   */
  async function vincular(account: Account, externalId: string | null) {
    setFailure(null)
    try {
      await accountRepository.update(account.id, { providerAccountId: externalId })
      reload()
    } catch (error: unknown) {
      console.error('AccountsScreen: o vínculo falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível vincular a conta.')
    }
  }

  async function setStatus(account: Account, status: Account['status']) {
    setFailure(null)
    try {
      await accountRepository.update(account.id, { status })
      reload()
    } catch (error: unknown) {
      console.error('AccountsScreen: a mudança de status falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível arquivar a conta.')
    }
  }

  return (
    <section aria-label="Contas">
      <h2 className="mb-2 text-sm font-semibold tracking-tight">Contas</h2>

      {state.status === 'loading' && (
        <p role="status" className="text-sm text-[var(--text-muted)]">
          Carregando as contas…
        </p>
      )}
      {state.status === 'failed' && (
        <p role="alert" className="text-sm text-[var(--text-muted)]">
          Não foi possível carregar as contas agora.
        </p>
      )}

      {state.status === 'loaded' && (
        <ul className="mb-4 text-sm">
          {state.items.length === 0 && (
            <li className="text-[var(--text-muted)]">Nenhuma conta cadastrada.</li>
          )}
          {state.items.map((account) => (
            <li key={account.id} className="flex flex-wrap items-baseline gap-2 border-b border-[var(--border)] py-1">
              <span className="font-medium">{account.name}</span>
              <span className="text-[var(--text-muted)]">
                {account.institution} · {ACCOUNT_TYPES[account.type]} · {STATUSES[account.status]}
              </span>
              <span>
                {account.balanceCents === null ? 'saldo desconhecido' : formatBRL(account.balanceCents)}
              </span>
              <button
                type="button"
                className="alvo justify-start underline"
                onClick={() => {
                  setEditingId(account.id)
                  setDraft(draftOf(account))
                  setFailure(null)
                }}
              >
                Editar {account.name}
              </button>
              {catalogo.state.status === 'loaded' && (
                <ProviderLinkField
                  kind="account"
                  label={`Vínculo com a Pluggy de ${account.name}`}
                  value={account.providerAccountId}
                  options={catalogo.state.items}
                  onChange={(externalId) => {
                    void vincular(account, externalId)
                  }}
                />
              )}
              <button
                type="button"
                className="alvo justify-start underline"
                onClick={() => {
                  void setStatus(account, account.status === 'archived' ? 'active' : 'archived')
                }}
              >
                {account.status === 'archived' ? 'Reativar' : 'Arquivar'} {account.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        aria-label={editingId === null ? 'Nova conta' : 'Editar conta'}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {failure !== null && <Problem>{failure}</Problem>}

        <TextField
          id={`${ID}-nome`}
          label="Nome"
          value={draft.name}
          onChange={(name) => {
            change({ name })
          }}
          placeholder="Nubank — Conta Corrente"
        />
        <TextField
          id={`${ID}-instituicao`}
          label="Instituição"
          value={draft.institution}
          onChange={(institution) => {
            change({ institution })
          }}
          placeholder="Nubank"
        />

        {/* `type` não está em `AccountPatch`: mudar o tipo de uma conta com
            histórico muda o significado dele, e a única mudança que faria
            sentido — virar 'cash' — é a que quebraria o invariante do saldo. */}
        {editingId === null && (
          <SelectField
            id={`${ID}-tipo`}
            label="Tipo"
            value={draft.type}
            options={optionsOf(ACCOUNT_TYPES).map((type) => ({
              value: type,
              label: ACCOUNT_TYPES[type],
            }))}
            onChange={(type) => {
              change({ type: type as Account['type'] })
            }}
          />
        )}

        {hasBalance ? (
          <TextField
            id={`${ID}-saldo`}
            label="Saldo (em branco = desconhecido)"
            value={draft.balance}
            onChange={(balance) => {
              change({ balance })
            }}
            hint={balanceProblem}
            placeholder="1.234,56"
          />
        ) : (
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            Dinheiro em espécie não tem saldo consultável.
          </p>
        )}

        <button type="submit" className="alvo mr-2 justify-start underline">
          {editingId === null ? 'Criar conta' : 'Salvar conta'}
        </button>
        {editingId !== null && (
          <button type="button" className="alvo justify-start underline" onClick={reset}>
            Cancelar edição
          </button>
        )}
      </form>
    </section>
  )
}
